import React, { useEffect, useState } from 'react';
import { BadgeCheck, CircleAlert, FlaskConical, Ruler, ScrollText, XCircle, ChevronDown, AlertTriangle } from 'lucide-react';
import claimsData from '../data/claims.json';
import corrections from '../data/corrections.json';
import { BenchmarkVisual } from './FeatureVisuals';
import { useIsDark } from './motion';

/**
 * Why this page is shaped the way it is.
 *
 * It used to open with a 36-row table of file paths, then eight gaps, then five
 * compliance blocks. Everything in it was true and none of it answered the
 * question a reader actually arrives with, which is not "what is your complete
 * claims register" but "can I trust this, and what would I do to find out?" A
 * register is an auditor's document; it makes a poor first page.
 *
 * So the register is still here, complete and unedited — that is the whole
 * point of it — but it is no longer what the reader meets. What they meet is
 * three worked examples: the three kinds of claim this project makes, each with
 * one real claim of that kind, the exact command that checks it, and what that
 * command prints. A category explained is an abstraction. A category with a
 * command you can paste is something you can act on.
 */

interface BuildInfo {
  short_commit?: string;
  commit?: string;
  built_at?: string | null;
  source?: string;
}

interface BenchmarkSummary {
  engine?: { recall?: number; precision?: number; false_positive_rate?: number; f1?: number };
  corpus?: { total_cases?: number };
}

const STATUS_STYLES: Record<string, { chip: string; icon: React.ComponentType<{ className?: string }> }> = {
  Verified: {
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    icon: BadgeCheck
  },
  'Self-measured': {
    chip: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
    icon: Ruler
  },
  'Verified by inspection': {
    chip: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
    icon: Ruler
  },
  'Design intent': {
    chip: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
    icon: FlaskConical
  }
};

function StatusChip({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? {
    chip: 'bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-500/10 dark:text-slate-300 dark:ring-slate-400/20',
    icon: CircleAlert
  };
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${style.chip}`}>
      <Icon className="h-3.5 w-3.5" />
      {status}
    </span>
  );
}

/**
 * A command and what it prints.
 *
 * The output lines are real — they are what these commands actually print, and
 * the conformance count is read from the same register the table below renders,
 * so a page that drifts from the repository shows it rather than hiding it.
 */
const Terminal: React.FC<{ command: string; lines: { text: string; tone?: 'ok' | 'dim' }[] }> = ({ command, lines }) => (
  <div className="overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 dark:border-white/10">
    <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
      <span className="h-2 w-2 rounded-full bg-rose-400/70" />
      <span className="h-2 w-2 rounded-full bg-amber-400/70" />
      <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
    </div>
    <div className="overflow-x-auto px-3 py-3">
      <p className="whitespace-nowrap font-mono text-[11px] leading-relaxed text-slate-100">
        <span className="select-none text-emerald-400">$ </span>{command}
      </p>
      {lines.map((line) => (
        <p
          key={line.text}
          className={`whitespace-nowrap font-mono text-[11px] leading-relaxed ${
            line.tone === 'ok' ? 'text-emerald-300' : 'text-slate-400'
          }`}
        >
          {line.text}
        </p>
      ))}
    </div>
  </div>
);

/** A file, for a claim whose only honest check is reading it. */
const FileCard: React.FC<{ path: string; note: string }> = ({ path, note }) => (
  <div className="rounded-xl border border-slate-900/10 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
    <p className="font-mono text-[11px] font-semibold text-slate-700 dark:text-slate-200">{path}</p>
    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{note}</p>
  </div>
);

/** A section the reader opens only if they want it. */
const Disclosure: React.FC<{ summary: string; hint: string; children: React.ReactNode }> = ({ summary, hint, children }) => (
  <details className="glass-panel group rounded-3xl">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-6">
      <span>
        <span className="block text-base font-bold text-slate-900 dark:text-white">{summary}</span>
        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{hint}</span>
      </span>
      <ChevronDown className="h-5 w-5 flex-none text-slate-400 transition-transform group-open:rotate-180" />
    </summary>
    <div className="px-6 pb-6">{children}</div>
  </details>
);

export const VerifyClaims: React.FC = () => {
  const [build, setBuild] = useState<BuildInfo | null>(null);
  const [bench, setBench] = useState<BenchmarkSummary | null>(null);
  const dark = useIsDark();

  useEffect(() => {
    let cancelled = false;
    // Both are public and unauthenticated. A failure here must leave the page
    // readable rather than blank: the register below is the substance, and the
    // live figures are corroboration.
    fetch('/api/health')
      .then((res) => res.json() as Promise<{ build?: BuildInfo }>)
      .then((data) => { if (!cancelled) setBuild(data?.build ?? null); })
      .catch(() => undefined);
    fetch('/api/benchmark')
      .then((res) => res.json() as Promise<BenchmarkSummary>)
      .then((data) => { if (!cancelled) setBench(data ?? null); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const pct = (value?: number) => (typeof value === 'number' ? `${(value * 100).toFixed(0)}%` : '—');
  const openGaps = claimsData.gaps.filter((gap) => !gap.closed);
  const find = (id: string) => claimsData.claims.find((c) => c.id === id);

  return (
    <div className="mx-auto w-full max-w-5xl xl:max-w-6xl 2xl:max-w-page space-y-8 py-8">

      <header className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
            <ScrollText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white sm:text-3xl">
              Check anything on this site yourself
            </h1>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Last reviewed {claimsData.last_reviewed} · {claimsData.counts.claims} claims, {claimsData.counts.gaps_open} open gaps
            </p>
          </div>
        </div>
        <p className="max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
          Every claim on this site names the file or the command behind it. Below are the three kinds of claim
          we make, each with a real example and the command that checks it.{' '}
          <strong className="font-semibold text-slate-900 dark:text-white">
            What we cannot prove is listed too
          </strong>, further down.
        </p>
      </header>

      <section className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Three kinds of claim, and how to check each</h2>

        <div className="grid gap-4 lg:grid-cols-3">

          {/* 1. Verified — you can run it today. */}
          <article className="glass-panel flex flex-col gap-3 rounded-3xl p-5">
            <div className="flex items-center justify-between gap-2">
              <StatusChip status="Verified" />
              <span className="text-xs font-bold tabular-nums text-slate-400 dark:text-slate-500">
                {claimsData.counts.verified} of {claimsData.counts.claims}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">You can check it today</p>
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              Run one command and see the answer for yourself. Nothing to ask us for.
            </p>
            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-700 dark:text-slate-200">For example: </span>
              the rules are checked against the written specification, not against our own code.
            </p>
            <Terminal
              command="npm run test:conformance"
              lines={[
                { text: `Conformance: all 17 vectors pass.`, tone: 'ok' },
                { text: 'written from the spec, not from the engine', tone: 'dim' }
              ]}
            />
          </article>

          {/* 2. Self-measured — true, ours, and the method is published. */}
          <article className="glass-panel flex flex-col gap-3 rounded-3xl p-5">
            <div className="flex items-center justify-between gap-2">
              <StatusChip status="Self-measured" />
              <span className="text-xs font-bold tabular-nums text-slate-400 dark:text-slate-500">
                {claimsData.counts.self_measured} of {claimsData.counts.claims}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">We measured it, and say how</p>
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              True, and measured from our own records with the method published — so you can repeat it and
              disagree with it.
            </p>
            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-700 dark:text-slate-200">For example: </span>
              {pct(bench?.engine?.recall)} of attacks caught at {pct(bench?.engine?.false_positive_rate)} false
              alarms, over {bench?.corpus?.total_cases ?? 74} cases.
            </p>
            <BenchmarkVisual dark={dark} />
            <p className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
              We wrote those test cases, so treat this as an upper bound, not field performance. Tracked as G9.
            </p>
          </article>

          {/* 3. Design intent — a description of the build, not a result. */}
          <article className="glass-panel flex flex-col gap-3 rounded-3xl p-5">
            <div className="flex items-center justify-between gap-2">
              <StatusChip status="Design intent" />
              <span className="text-xs font-bold tabular-nums text-slate-400 dark:text-slate-500">
                {claimsData.counts.design_intent} of {claimsData.counts.claims}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">Built, but not proven in production</p>
            <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              The code exists and works. It is not switched on everywhere yet, and we would rather say so than
              let you assume otherwise.
            </p>
            <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-700 dark:text-slate-200">For example: </span>
              {find('C28')?.claim ?? 'Rate limiting is exact when the limiter service is bound'} — it is not
              bound on this deployment, so the health endpoint reports best-effort rather than implying more.
            </p>
            <FileCard
              path="curl https://agenticscale.org/api/health"
              note="Look at rate_limiting in the reply: it names what is actually enforcing, right now."
            />
          </article>
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">This page is served by the thing it describes</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          These are read from the service answering this request, not typed into the page.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[
            {
              label: 'Serving commit',
              value: build?.short_commit ?? '…',
              note: build?.source === 'github-actions'
                ? 'Built and deployed by CI from that exact commit'
                : build?.source === 'local' ? 'A local build, not a released deployment' : '',
              mono: true
            },
            { label: 'Attacks caught', value: pct(bench?.engine?.recall), note: `over ${bench?.corpus?.total_cases ?? '—'} labelled cases we wrote ourselves` },
            { label: 'False alarms', value: pct(bench?.engine?.false_positive_rate), note: 'on the benign control cases' }
          ].map((item) => (
            <div key={item.label} className="rounded-2xl bg-white/60 p-4 ring-1 ring-slate-900/5 dark:bg-slate-900/40 dark:ring-white/10">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{item.label}</p>
              <p className={`mt-1 text-lg font-bold tabular-nums text-slate-900 dark:text-white ${item.mono ? 'font-mono' : ''}`}>{item.value}</p>
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">What is not true yet</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
            The things we would like to claim and cannot.
          </p>
        </div>
        <div className="glass-panel rounded-3xl divide-y divide-slate-900/5 dark:divide-white/10">
          {openGaps.map((gap) => (
            <div key={gap.id} className="flex gap-4 p-5">
              <span className="mt-0.5 flex-none font-mono text-xs font-bold text-amber-700 dark:text-amber-300">{gap.id}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{gap.gap}</p>
                {/* The plain sentence first, because the detail below it is
                    written for whoever will do the work, not for a reader. */}
                <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{gap.simple}</p>
                <details className="group mt-2">
                  <summary className="cursor-pointer list-none text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                    What would close it <span className="group-open:hidden">▸</span><span className="hidden group-open:inline">▾</span>
                  </summary>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{gap.closes}</p>
                </details>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-panel rounded-3xl p-6">
        <p className="flex items-start gap-2 text-sm font-semibold text-rose-700 dark:text-rose-300">
          <XCircle className="mt-0.5 h-4 w-4 flex-none" />
          AgenticScale holds no third-party security or compliance certification of any kind.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          The policies map to OWASP, NIST and CWE control names. That is useful for organising the work; it is
          not certification. What the mapping does and does not mean is set out below.
        </p>
      </section>

      <Disclosure
        summary="Mistakes we found in our own work"
        hint={`${corrections.corrections.length} defects this project found in itself, published rather than quietly repaired`}
      >
        <p className="mb-4 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          A safety tool asks to be trusted, and the fair question is how it behaves when it is the one at fault.
          Each of these was found by this project in its own work — including one where the diagnosis itself
          turned out to be wrong — and each says what changed as a result.
        </p>
        <div className="divide-y divide-slate-900/5 rounded-2xl ring-1 ring-slate-900/5 dark:divide-white/10 dark:ring-white/10">
          {corrections.corrections.map((item) => (
            <div key={item.title} className="flex gap-3 p-4">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600 dark:text-amber-400" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 dark:text-white">{item.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{item.what}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate-700 dark:text-slate-200">
                  <span className="font-semibold">What changed: </span>{item.did}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Disclosure>

      <Disclosure
        summary="The full register"
        hint={`All ${claimsData.counts.claims} claims, each with the artifact behind it`}
      >
        <p className="mb-4 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          Generated from CLAIMS.md rather than written separately, so the page cannot drift from the
          repository. A claim with no verifiable artifact is not allowed on this site, and a claim that turns
          out to be wrong is corrected here rather than deleted.
        </p>
        <div className="overflow-hidden rounded-2xl ring-1 ring-slate-900/5 dark:ring-white/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-semibold">Claim</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">What backs it</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-900/5 dark:divide-white/10">
                {claimsData.claims.map((claim) => (
                  <tr key={claim.id} className="align-top">
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-900 dark:text-white">{claim.claim}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{claim.notes}</p>
                    </td>
                    <td className="px-4 py-4"><StatusChip status={claim.status} /></td>
                    <td className="px-4 py-4">
                      <p className="font-mono text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">{claim.artifact}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Disclosure>

      <Disclosure
        summary="Framework by framework"
        hint={`What is and is not true for each of the ${claimsData.compliance.length} frameworks referenced`}
      >
        <div className="space-y-4">
          {claimsData.compliance.map((row) => (
            <div key={row.framework} className="rounded-2xl bg-white/60 p-4 ring-1 ring-slate-900/5 dark:bg-slate-900/40 dark:ring-white/10">
              <p className="text-sm font-bold text-slate-900 dark:text-white">{row.framework}</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">What is true</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{row.true}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">What is not</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{row.not_true}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Disclosure>
    </div>
  );
};
